import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { collection, onSnapshot } from 'firebase/firestore';

import '../Services.css';

function Services() {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [groupedItems, setGroupedItems] = useState({});
  const [loadingItems, setLoadingItems] = useState(true);

  const navigate = useNavigate();

  const handleBookNow = () => {
    const userPhone = localStorage.getItem('userPhone');

    if (!auth.currentUser || !userPhone) {
      window.dispatchEvent(new Event('openAuthModal'));
      return;
    }

    navigate('/booking');
  };

  // 🎯 Fetch Dynamic Items and Group by Service Name
  useEffect(() => {
    const itemsRef = collection(db, 'items');

    const unsubscribe = onSnapshot(
      itemsRef,
      (snapshot) => {
        const groups = {};

        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          const service = data.serviceName || 'General Services';

          if (!groups[service]) {
            groups[service] = [];
          }

          groups[service].push({
            id: docSnap.id,
            name: data.name || 'Unnamed Item',
            economyPrice: data.economyPrice ?? data.price ?? 0,
            standardPrice: data.standardPrice ?? data.price ?? 0,
            premiumPrice: data.premiumPrice ?? data.price ?? 0,
          });
        });

        setGroupedItems(groups);
        setLoadingItems(false);
      },
      (error) => {
        console.error("Error fetching items for pricing:", error);
        setLoadingItems(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const serviceList = [
    {
      title: "Wash & Fold",
      desc: "Perfect for everyday wear. We wash, dry, and neatly fold your clothes so they are ready for the drawer.",
      img: "https://images.unsplash.com/photo-1545173168-9f1947eebb7f?q=80&w=500",
      icon: "fas fa-sync-alt"
    },
    {
      title: "Wash & Press",
      desc: "The complete package. Your garments are professionally laundered and pressed for a crisp, professional look.",
      img: "https://images.unsplash.com/photo-1517677208171-0bc6725a3e60?q=80&w=500",
      icon: "fas fa-tshirt"
    },
    {
      title: "Steam Press",
      desc: "Remove every wrinkle with our professional steam pressing service, ideal for shirts, suits, and formal wear.",
      img: "https://images.unsplash.com/photo-1489274495757-95c7c837b101?q=80&w=500",
      icon: "fas fa-wind"
    },
    {
      title: "Dry Cleaning",
      desc: "Deep cleaning for delicate fabrics using eco-friendly solutions to preserve texture and color.",
      img: "https://images.unsplash.com/photo-1591195853828-11db59a44f6b?q=80&w=400",
      icon: "fas fa-gem"
    },
    {
      title: "Shoe Cleaning",
      desc: "Give your sneakers or formal shoes a new life. We deep clean and sanitize all types of footwear.",
      img: "https://images.unsplash.com/photo-1653868249587-284b275a1c67?q=80&w=904&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      icon: "fas fa-shoe-prints"
    },
    {
      title: "Curtain & Carpet",
      desc: "Specialized heavy-duty cleaning to remove dust, allergens, and stains from your home fabrics.",
      img: "https://images.unsplash.com/photo-1648115315559-620fb2f655aa?q=80&w=774&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      icon: "fas fa-couch"
    },
    {
      title: "Sofa Cleaning",
      desc: "On-site deep sofa cleaning using safe chemicals to refresh your living room and remove deep-seated dirt.",
      img: "https://images.unsplash.com/photo-1686178827149-6d55c72d81df?q=80&w=870&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      icon: "fas fa-chair"
    },
    {
      title: "Bag Cleaning",
      desc: "Premium care for your luxury bags and backpacks. We handle leather and fabric with utmost precision.",
      img: "https://images.unsplash.com/photo-1548036328-c9fa89d128fa?q=80&w=500",
      icon: "fas fa-shopping-bag"
    }
  ];

  return (
    <div className="services-page">
      {/* Hero Section */}
      <section className="services-hero">
        <h1>Our Premium <span>Services & Pricing</span></h1>
        <p>From everyday laundry to specialized garment care, we provide the best cleaning solutions in the UAE.</p>
      </section>

      {/* Services Grid */}
      <section className="services-grid-container">
        <div className="services-grid">
          {serviceList.map((service, index) => (
            <div className="service-card" key={index}>
              <div className="service-img-box">
                <img src={service.img} alt={service.title} />
                <div className="service-icon-badge">
                  <i className={service.icon}></i>
                </div>
              </div>
              <div className="service-info">
                <h3>{service.title}</h3>
                <p>{service.desc}</p>
                <button
                  className="btn-service-book"
                  onClick={handleBookNow}
                >
                  Book Now
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Dynamic Pricing Section */}
      <section className="pricing-section">
        <div className="section-title-center">
          <span>Transparent Pricing</span>
          <h2>Our Service Rates</h2>
          <p>Affordable and premium laundry care services across UAE.</p>
        </div>

        {loadingItems ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#0056B3', fontWeight: 'bold' }}>
            Loading Service Rates...
          </div>
        ) : Object.keys(groupedItems).length > 0 ? (
          <div className="pricing-grid">
            {Object.entries(groupedItems).map(([serviceTitle, items]) => (
              <div className="pricing-card" key={serviceTitle}>
                <h3>{serviceTitle}</h3>
                <table>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #eef2f5', fontSize: '12px', color: '#666' }}>
                      <th style={{ textAlign: 'left', paddingBottom: '8px' }}>Item</th>
                      <th style={{ textAlign: 'center', paddingBottom: '8px' }}>Eco</th>
                      <th style={{ textAlign: 'center', paddingBottom: '8px' }}>Std</th>
                      <th style={{ textAlign: 'right', paddingBottom: '8px' }}>Prem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.id}>
                        <td><strong>{item.name}</strong></td>
                        <td style={{ textAlign: 'center' }}>AED {item.economyPrice}</td>
                        <td style={{ textAlign: 'center' }}>AED {item.standardPrice}</td>
                        <td style={{ textAlign: 'right' }}>AED {item.premiumPrice}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '30px', color: '#888' }}>
            No pricing items available at the moment.
          </div>
        )}
      </section>
    </div>
  );
}

export default Services;